import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Instagram, Facebook } from "lucide-react";
import { SITE } from "../data/siteData";

const programLinks = [
  { to: "/programs", label: "All Programs" },
  { to: "/services/board-and-train", label: "Board & Train" },
  { to: "/services/private-training", label: "Private Training" },
  { to: "/virtual-dog-training", label: "Virtual Lessons" },
  { to: "/services/jumpstart", label: "Jumpstart Program" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setProgramsOpen(false);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `text-[13px] 2xl:text-sm font-medium tracking-wide whitespace-nowrap transition-colors hover:text-brand ${isActive ? "text-brand" : "text-white/80"}`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color] duration-300 ${
        scrolled || open ? "bg-obsidian/95 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 2xl:px-6 h-20 grid grid-cols-[1fr_auto_1fr] items-center">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 justify-self-start">
          <img src={SITE.logo} alt="Chinook K9 logo" className="h-12 w-auto" />
        </Link>

        <nav className="hidden xl:flex items-center justify-center gap-4 2xl:gap-7">
          <NavLink to="/" className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">About</NavLink>
          <div className="relative">
            <button
              data-testid="nav-programs-toggle"
              onClick={() => setProgramsOpen((v) => !v)}
              className="flex items-center gap-1 text-[13px] 2xl:text-sm font-medium tracking-wide whitespace-nowrap text-white/80 hover:text-brand transition-colors"
            >
              Programs <ChevronDown size={14} className={`transition-transform ${programsOpen ? "rotate-180" : ""}`} />
            </button>
            {programsOpen && (
              <div className="absolute top-full left-0 mt-3 w-56 bg-[#121212] border border-white/10 rounded-lg shadow-2xl py-2" data-testid="nav-programs-menu">
                {programLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} className="block px-5 py-2.5 text-sm text-white/80 hover:text-brand hover:bg-white/5 transition-colors" data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
          <NavLink to="/dog-training/calgary" className={linkCls} data-testid="nav-areas">Service Areas</NavLink>
          <NavLink to="/gallery" className={linkCls} data-testid="nav-gallery">Gallery</NavLink>
          <NavLink to="/blog" className={linkCls} data-testid="nav-blog">Blog</NavLink>
          <NavLink to="/faqs" className={linkCls} data-testid="nav-faqs">FAQ</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden xl:flex items-center gap-4 justify-self-end">
          <Link
            to="/services/board-and-train"
            data-testid="nav-start-today-btn"
            className="bg-brand text-white text-sm font-bold px-5 py-2.5 rounded-full hover:bg-red-700 transition-colors shadow-[0_0_15px_rgba(220,38,38,0.4)] whitespace-nowrap"
          >
            Board & Training!
          </Link>
          <a href={SITE.phoneHref} data-testid="nav-phone" className="flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-brand transition-colors whitespace-nowrap">
            <Phone size={15} /> {SITE.phone}
          </a>
          <div className="flex items-center gap-3">
            <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="nav-instagram" className="text-white/70 hover:text-brand transition-colors" aria-label="Instagram">
              <Instagram size={16} />
            </a>
            <a href={SITE.facebook} target="_blank" rel="noreferrer" data-testid="nav-facebook" className="text-white/70 hover:text-brand transition-colors" aria-label="Facebook">
              <Facebook size={16} />
            </a>
          </div>
        </div>

        <button data-testid="nav-mobile-toggle" className="xl:hidden text-white col-start-3 justify-self-end" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-obsidian/95 backdrop-blur-md border-t border-white/10 px-6 py-6 space-y-4 text-right flex flex-col items-end" data-testid="nav-mobile-menu">
          <NavLink to="/" className="block text-white/90 font-medium" data-testid="nav-mobile-home">Home</NavLink>
          <NavLink to="/about" className="block text-white/90 font-medium" data-testid="nav-mobile-about">About</NavLink>
          {programLinks.map((l) => (
            <NavLink key={l.to} to={l.to} className="block text-white/70 pr-3 border-r-2 border-brand/50" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to="/dog-training/calgary" className="block text-white/90 font-medium" data-testid="nav-mobile-areas">Service Areas</NavLink>
          <NavLink to="/gallery" className="block text-white/90 font-medium" data-testid="nav-mobile-gallery">Gallery</NavLink>
          <NavLink to="/blog" className="block text-white/90 font-medium" data-testid="nav-mobile-blog">Blog</NavLink>
          <NavLink to="/faqs" className="block text-white/90 font-medium" data-testid="nav-mobile-faqs">FAQ</NavLink>
          <NavLink to="/contact" className="block text-white/90 font-medium" data-testid="nav-mobile-contact">Contact</NavLink>
          <a href={SITE.phoneHref} className="flex items-center gap-2 text-brand font-bold" data-testid="nav-mobile-phone">
            <Phone size={16} /> {SITE.phone}
          </a>
          <Link to="/services/board-and-train" data-testid="nav-mobile-start-today" className="inline-block bg-brand text-white font-bold px-6 py-3 rounded-full">
            Board & Training!
          </Link>
          <div className="flex items-center gap-4 pt-1">
            <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="nav-mobile-instagram" className="text-white/70 hover:text-brand transition-colors" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a href={SITE.facebook} target="_blank" rel="noreferrer" data-testid="nav-mobile-facebook" className="text-white/70 hover:text-brand transition-colors" aria-label="Facebook">
              <Facebook size={18} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
